import { axiosFetch as authFetch } from './axiosFetch';
import { CREDENTIALS } from '../constants';
import {
    chatbotStartSessionPayloadInterface,
    classifyQueryPayloadInterface,
    metricsChatPayloadInterface,
    insightsPayloadInterface,
    MerchantInfoPayloadInterface,
    ChatHistoryPayloadInterface,
    SessionResumePayloadInterface,
    StoreMessagePayloadInterface,
} from '../interface/ReportsChatbotInterface';

const CHATBOT_BASE_URL = process.env.REACT_APP_REPORTS_CHATBOT_API_ENDPOINT || '';

function getChatbotToken(passedToken?: string | null): string | null {
    if (passedToken && typeof passedToken === 'string' && passedToken.trim()) return passedToken.trim();
    try {
        const raw = localStorage.getItem(CREDENTIALS);
        if (!raw) return null;
        const stored = JSON.parse(raw);
        const cred = stored?.credentials ?? stored;
        const t = cred?.accessToken ?? cred?.access_token ?? cred?.token ?? cred?.idToken ?? cred?.id_token ?? cred?.jwtToken ?? cred?.jwt;
        return t && typeof t === 'string' && t.trim() ? t.trim() : null;
    } catch {
        return null;
    }
}

const chatbotFetch = async (url: string, options: { method: string; body?: any; token?: string | null }): Promise<any> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' };
    const authToken = getChatbotToken(options.token);
    if (authToken) {
        headers['Authorization'] = `bearer ${authToken}`;
    }

    const fetchOptions: RequestInit = { method: options.method, headers };
    if (options.body) {
        fetchOptions.body = JSON.stringify(options.body);
    }

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
        const errText = await response.text().catch(() => '');
        console.error('[chatbotReportsApi] ERROR =>', { url, status: response.status, errText: errText?.substring(0, 200) });
        let errMsg = 'Failed to fetch chatbot data';
        try { errMsg = JSON.parse(errText)?.message || errMsg; } catch { /* ignore */ }
        if (response.status === 403) errMsg = errMsg || 'Access denied. Please sign in again.';
        throw new Error(errMsg);
    }

    return response.json();
};

const buildQuery = (payload: any): string => {
    const p = payload || {};
    const params = new URLSearchParams();
    const loc = p.locationId ?? p.locationid;
    if (loc) params.set('locationid', loc);
    if (p.startDate) params.set('startDate', p.startDate);
    if (p.endDate) params.set('endDate', p.endDate);
    if (p.tablePageNo != null) params.set('page', p.tablePageNo);
    else if (p.page != null) params.set('page', p.page);
    if (p.tableRecordLimit != null) params.set('size', p.tableRecordLimit);
    else if (p.size != null) params.set('size', p.size);
    if (p.searchQuery != null) params.set('search', p.searchQuery);
    else if (p.search != null) params.set('search', p.search);
    if (p.itemIds?.length > 0) params.set('itemIds', (p.itemIds || []).join(','));
    else if (p.categoryIds?.length > 0) params.set('categoryIds', (p.categoryIds || []).join(','));
    else if (p.groupByCategory != null) params.set('groupByCategory', p.groupByCategory);
    if (p.reason != null && p.reason !== '') params.set('reason', p.reason);
    if (p.offer) params.set('offer', p.offer);
    if (p.chartSliceName) params.set('chartSliceName', p.chartSliceName);
    if (p.type) params.set('type', p.type);
    if (p.considerThirdPartyValues != null) params.set('considerThirdPartyValues', p.considerThirdPartyValues);
    if (p.sortBy != null && p.sortBy !== '') params.set('sortBy', p.sortBy);
    if (p.sortAsc != null) params.set('sortAsc', p.sortAsc);
    if (p.paymentType != null && p.paymentType !== '') params.set('paymentType', p.paymentType);
    if (p.categoryId != null && p.categoryId !== '') params.set('categoryId', p.categoryId);
    if (p.uncategoryItems) params.set('uncategoryItems', p.uncategoryItems);
    if (p.paid != null) params.set('paid', String(p.paid));
    if (p.merchant_id != null && p.merchant_id !== '') params.set('merchant_id', p.merchant_id);
    if (p.location_id != null && p.location_id !== '') params.set('location_id', p.location_id);
    if (p.user_role != null && p.user_role !== '') params.set('user_role', p.user_role);
    if (p.user_id != null && p.user_id !== '') params.set('user_id', p.user_id);
    return params.toString();
};

// POST /start_session
export const chatbotStartSessionApi = async (payload: chatbotStartSessionPayloadInterface, token?: string | null) =>
    chatbotFetch(`${CHATBOT_BASE_URL}/start_session`, { method: 'POST', body: payload, token });

// GET /merchant_info
export const sendMerchantInfoApi = async (payload: MerchantInfoPayloadInterface, token?: string | null) => {
    const q = buildQuery(payload);
    return chatbotFetch(`${CHATBOT_BASE_URL}/merchant_info?${q}`, { method: 'GET', token });
};

// POST /classify_query
export const classifyQueryApi = async (payload: classifyQueryPayloadInterface, token?: string | null) =>
    chatbotFetch(`${CHATBOT_BASE_URL}/classify_query`, { method: 'POST', body: payload, token });

// POST /generate_kpi_data
export const metricsApi = async (payload: metricsChatPayloadInterface, token?: string | null) =>
    chatbotFetch(`${CHATBOT_BASE_URL}/generate_kpi_data`, { method: 'POST', body: payload, token });

// POST /generate_insight
export const insightsApi = async (payload: insightsPayloadInterface, token?: string | null) =>
    chatbotFetch(`${CHATBOT_BASE_URL}/generate_insight`, { method: 'POST', body: payload, token });

// GET /history
export const chatHistoryApi = async (payload: ChatHistoryPayloadInterface, token?: string | null) =>
    chatbotFetch(`${CHATBOT_BASE_URL}/history/${payload.user_role}?locationid=${payload.locationid}`, { method: 'GET', token });

// POST /session/{session_id}/resume?current_session_id=...
export const resumeSessionApi = async (payload: SessionResumePayloadInterface, token?: string | null) =>
    chatbotFetch(
        `${CHATBOT_BASE_URL}/session/${payload.session_id}/resume?current_session_id=${encodeURIComponent(payload.current_session_id)}`,
        { method: 'POST', token }
    );

// POST /store_message
export const storeMessageApi = async (payload: StoreMessagePayloadInterface, token?: string | null) =>
    chatbotFetch(`${CHATBOT_BASE_URL}/store_message`, { method: 'POST', body: payload, token });
